import { supabase } from './supabase';

/**
 * Processes invoice documents via the secure Edge Function
 * The API key is stored server-side, never exposed to the client
 *
 * @param {string} prompt - Instructions for invoice processing
 * @param {File} imageFile - Invoice image/PDF file
 * @param {AbortSignal} signal - Optional abort signal for cancellation
 * @param {string} modelName - Optional model name (default: gemini-2.0-flash)
 * @returns {Promise<Object>} Structured invoice data
 */
export async function parseInvoiceWithAI(prompt, imageFile, signal = null, modelName = 'gemini-2.0-flash') {
  try {
    if (!imageFile) {
      throw new Error('Please provide an invoice file to process.');
    }

    // Convert image file to base64
    const imageBase64 = await convertFileToBase64(imageFile);

    // Get the current session for authorization
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      throw new Error('Authentication required. Please sign in again.');
    }

    // Call the Edge Function directly (no client-side rate limiting for now)
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/parse-invoice`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          imageBase64,
          mimeType: imageFile.type,
          prompt,
          modelName: modelName || 'gemini-2.0-flash',
        }),
        signal,
      }
    );

    // Handle rate limiting from Gemini API
    if (response.status === 429) {
      const retryAfter = response.headers.get('Retry-After') || '60';
      const error = new Error(`Rate limit exceeded. Please wait ${retryAfter} seconds before trying again.`);
      error.status = 429;
      error.retryAfter = parseInt(retryAfter);
      throw error;
    }

    // Handle authentication errors
    if (response.status === 401) {
      throw new Error('Session expired. Please sign in again.');
    }

    // Handle service unavailable
    if (response.status === 503) {
      throw new Error('The AI model is currently overloaded. Please wait a few moments and try again.');
    }

    // Handle other errors
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `Request failed with status ${response.status}`);
    }

    // Parse and return the response
    const parsedData = await response.json();

    // Check if the Edge Function returned an error in the response body
    if (parsedData.error && !parsedData.vendor_name) {
      throw new Error(parsedData.message || parsedData.error);
    }

    return parsedData;

  } catch (error) {
    // Re-throw abort errors as-is for proper cancellation handling
    if (error.name === 'AbortError') {
      throw new Error('Request was cancelled by user.');
    }

    throw error;
  }
}

/**
 * Converts file to base64 format
 * @param {File} file - File to convert
 * @returns {Promise<string>} Base64 string (without data URL prefix)
 */
function convertFileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64Data = reader.result.split(',')[1];
      resolve(base64Data);
    };
    reader.onerror = (error) => reject(error);
  });
}

/**
 * Handles common API errors with user-friendly messages
 * @param {Error} error - The error object from the API
 * @returns {string} User-friendly error message
 */
export function handleGeminiError(error) {
  const errorMessage = error?.message || '';

  if (errorMessage.includes('overloaded') || errorMessage.includes('503')) {
    return 'The AI model is currently overloaded. Please wait a few moments and try again.';
  }

  if (errorMessage.includes('Rate limit') || errorMessage.includes('429')) {
    return 'Rate limit exceeded. Please wait a moment before trying again.';
  }

  if (errorMessage.includes('SAFETY') || errorMessage.includes('blocked')) {
    return 'Content was blocked by safety filters. Please modify your request.';
  }

  if (errorMessage.includes('cancelled')) {
    return 'Request was cancelled by user.';
  }

  if (errorMessage.includes('timeout')) {
    return 'Request timed out. Please try again.';
  }

  if (errorMessage.includes('Session expired') || errorMessage.includes('Authentication')) {
    return 'Your session has expired. Please sign in again.';
  }

  if (errorMessage.includes('Service configuration')) {
    return 'The service is not properly configured. Please contact support.';
  }

  return error?.message || 'An unexpected error occurred during invoice processing. Please try again.';
}

export default { parseInvoiceWithAI, handleGeminiError };
